import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ALIGNMENTS,
  CHARACTER_CLASSES,
  RACES,
  systemLabelKey,
} from "../../catalogs/characterSystemCatalog";
import Input from "../utils/Input/Input";
import ClassManagerModal from "./ClassManagerModal";

const CharacterInfo = ({ character, onChange }) => {
  const { t } = useTranslation();
  const [isClassManagerOpen, setIsClassManagerOpen] = useState(false);
  const characterClasses = character.characterClass?.length
    ? character.characterClass
    : [{ id: CHARACTER_CLASSES[0].id, classLevel: 0 }];
  const hasClassLevelMismatch = Boolean(character.ruleDerived?.classLevelMismatch);
  const classSettingsLabel = hasClassLevelMismatch
    ? t("character.classLevelMismatch", {
      allocated: character.ruleDerived.classLevelTotal,
      total: character.level.level,
    })
    : t("character.openClassSettings");

  const handleChange = (event) => {
    const { name, value } = event.target;
    const updatedFormData = { ...character };
    const levels = name.split(".");
    let currentLevel = updatedFormData;
    for (let index = 0; index < levels.length; index += 1) {
      if (index === levels.length - 1) {
        currentLevel[levels[index]] = value;
      } else {
        if (!currentLevel[levels[index]]) currentLevel[levels[index]] = {};
        currentLevel = currentLevel[levels[index]];
      }
    }
    onChange(updatedFormData);
  };

  const updateClasses = (nextClasses) => {
    onChange({ ...character, characterClass: nextClasses });
  };

  const systemSelect = (name, value, options, group, onSelect = handleChange) => {
    const localizedOptions = options.map((option) => ({
      ...option,
      label: t(systemLabelKey(group, option.id)),
    }));
    const longestLabelLength = Math.max(
      ...localizedOptions.map((option) => option.label.length)
    );
    const alignmentWidth = name === "alignmentId"
      ? `${Math.min(32, Math.max(18, longestLabelLength + 2))}ch`
      : undefined;

    return (
      <select
        className="custom-input"
        name={name}
        style={alignmentWidth ? { width: alignmentWidth } : undefined}
        value={value}
        onChange={onSelect}
      >
        {localizedOptions.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>
    );
  };

  return (
    <div className="quarter">
      <div className="character-info-container">
        <Input label={t("character.name")} id="characterName" name="characterName" value={character.characterName} className="name-input" onChange={handleChange} />
        <table>
          <tbody>
            <tr>
              <td>{t("character.background")}</td>
              <td><Input id="background" name="background" value={character.background} className="custom-input" onChange={handleChange} /></td>
              <td>{t("character.race")}</td>
              <td>{systemSelect("race.id", character.race.id, RACES, "races")}</td>
              <td>{t("character.level")}</td>
              <td><Input id="level" max="20" min="1" name="level.level" type="number" value={character.level.level} className="custom-input" onChange={handleChange} /></td>
            </tr>
            <tr>
              <td>{t("character.alignment")}</td>
              <td>{systemSelect("alignmentId", character.alignmentId, ALIGNMENTS, "alignments")}</td>
              <td>{t("character.class")}</td>
              <td>
                <div className="character-class-field">
                  {systemSelect(
                    "primaryClass",
                    characterClasses[0].id,
                    CHARACTER_CLASSES,
                    "classes",
                    (event) => updateClasses([
                      { ...characterClasses[0], id: event.target.value },
                      ...characterClasses.slice(1).filter((entry) => entry.id !== event.target.value),
                    ])
                  )}
                  <button
                    aria-label={classSettingsLabel}
                    className={`class-settings-button${hasClassLevelMismatch ? " class-settings-button-warning" : ""}`}
                    title={classSettingsLabel}
                    type="button"
                    onClick={() => setIsClassManagerOpen(true)}
                  >
                    <span aria-hidden="true">⚙</span>
                    {hasClassLevelMismatch && <span aria-hidden="true" className="class-settings-warning-mark">!</span>}
                  </button>
                </div>
              </td>
              <td>{t("character.xp")}</td>
              <td><Input id="currentXP" name="level.currentXP" value={character.level.currentXP} className="custom-input" onChange={handleChange} /></td>
            </tr>
          </tbody>
        </table>
      </div>

      <ClassManagerModal
        characterLevel={character.level.level}
        classes={characterClasses}
        isOpen={isClassManagerOpen}
        onChange={updateClasses}
        onClose={() => setIsClassManagerOpen(false)}
      />
    </div>
  );
};

export default CharacterInfo;
